<template>
  <CustomTable ref="table" class="compact-table" :elements=elements :data="data" :searchOptions="searchOptions" :loadData="loadData">
    <template v-slot:header-options>
      <button class="button-icon" title="Ajouter un fournisseur" @click="$refs.supplierModal.openModal()"><span class="fas fa-plus"></span></button>
    </template>
    <template v-slot:table>
      <thead v-if="$refs.table">
        <tr>
          <custom-header field="name" class="col-fill">Nom</custom-header>
          <custom-header field="siren" class="hide-for-mobile col-fit">SIREN</custom-header>
          <custom-header field="email" class="hide-for-mobile col-fit">Contact</custom-header>
          <custom-header field="paymentTermDays" class="hide-for-mobile col-fit">Délai de paiement</custom-header>
          <custom-header field="purchaseCount" class="hide-for-mobile col-fit" :sortable="false">Achats</custom-header>
          <custom-header field="ttcAmount" class="col-fit" :sortable="false">Total TTC</custom-header>
          <custom-header field="remainingAmount" class="col-fit" :sortable="false">Reste à payer</custom-header>
          <custom-header field="actions" class="col-fit" :sortable="false">Actions</custom-header>
        </tr>
      </thead>
      <tbody v-if="data && data.length > 0">
        <tr v-for="supplier in $refs.table.$refs.searchArray.simpleFilter(data)" :key="supplier.id" class="business-row" @click="$refs.supplierDetailModal.openModal(supplier)">
          <custom-body field="name" class="col-fill" :title="supplier.name">
            {{ supplier.name }}
            <span v-if="supplier.summary.toReviewCount > 0" class="to-review" :title="supplier.summary.toReviewCount + ' achat(s) à vérifier'">{{ supplier.summary.toReviewCount }} à vérifier</span>
          </custom-body>
          <custom-body field="siren" class="hide-for-mobile col-fit">{{ formatSiren(supplier.siren) }}</custom-body>
          <custom-body field="email" class="hide-for-mobile col-fit">{{ contactOf(supplier) }}</custom-body>
          <custom-body field="paymentTermDays" class="hide-for-mobile col-fit">{{ supplier.paymentTermDays !== undefined ? supplier.paymentTermDays + ' j' : '-' }}</custom-body>
          <custom-body field="purchaseCount" class="hide-for-mobile col-fit amount">{{ supplier.summary.purchaseCount }}</custom-body>
          <custom-body field="ttcAmount" class="col-fit amount">{{ formatAmount(supplier.summary.ttcAmount) }}</custom-body>
          <custom-body field="remainingAmount" class="col-fit amount">
            <span :class="{overdue: supplier.summary.overdueAmount > 0}" :title="supplier.summary.overdueAmount > 0 ? formatAmount(supplier.summary.overdueAmount) + ' en retard' : ''">{{ formatAmount(supplier.summary.remainingAmount) }}</span>
          </custom-body>
          <custom-body field="actions" class="col-fit">
            <button class="button-icon-custom" title="Modifier" @click.stop="$refs.supplierModal.openModal(supplier)"><span class="fas fa-pen-to-square"></span></button>
            <button class="button-icon-custom" title="Supprimer" @click.stop="remove(supplier)"><span class="fas fa-trash-can"></span></button>
          </custom-body>
        </tr>
      </tbody>
      <tbody v-else><tr class="empty-row"><td colspan="8" style="text-align: center">Aucun fournisseur enregistré</td></tr></tbody>
    </template>
  </CustomTable>
  <SupplierModal ref="supplierModal" :callback=loadData />
  <SupplierDetailModal ref="supplierDetailModal" :callback=loadData />
  <ConfirmModal ref="deleteConfirmModal" title="Suppression" text="Supprimer ce fournisseur ? Il ne doit plus avoir d'achat enregistré." yes="Supprimer" no="Retour"/>
</template>

<script>
import { library } from '@fortawesome/fontawesome-svg-core';
import { faPenToSquare, faPlus, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import CustomTable from "@/components/CustomTable.vue";
import CustomHeader from "@/components/tables/CustomHeader.vue";
import CustomBody from "@/components/tables/CustomBody.vue";
import ConfirmModal from "@/components/modals/confirmModal.vue";
import {NotificationService} from "@vues/v-common";
import {SORT_OPERATOR_ENUM} from "@/models/sortOperatorEnum";
import {formatIdentifier, SIREN_MASK} from "@/utils";
import {SupplierController} from "@/purchase/controllers/SupplierController";
import SupplierModal from "@/purchase/components/modals/SupplierModal.vue";
import SupplierDetailModal from "@/purchase/components/modals/SupplierDetailModal.vue";
import {PURCHASE_CATEGORY_ENUM, formatAmount, idOf} from "@/purchase/utils";
library.add(faPenToSquare, faPlus, faTrashCan);

// Totaux d'un fournisseur sans achat
const EMPTY_SUMMARY = {purchaseCount: 0, toReviewCount: 0, ttcAmount: 0, remainingAmount: 0, overdueAmount: 0};

export default {
  name: 'SupplierTable',
  components: {SupplierDetailModal, SupplierModal, ConfirmModal, CustomBody, CustomHeader, CustomTable},
  data() {
    return {
      data: [],
      elements: [
        {label: "Nom", value: "name", type: "text"},
        {label: "SIREN", value: "siren", type: "text"},
        {label: "Email", value: "email", type: "text"},
        {label: "Téléphone", value: "phone", type: "text"},
        {label: "Délai de paiement (jours)", value: "paymentTermDays", type: "number"},
        {label: "Catégorie habituelle", value: "defaultCategory", type: "select", values: PURCHASE_CATEGORY_ENUM},
        {label: "Actions", value: undefined, type: undefined},
      ],
      searchOptions: {
        filters: [],
        sorts: [{field: "name", order: SORT_OPERATOR_ENUM.ASCENDING.value}]
      }
    };
  },
  async mounted() {
    this.$refs.table.$refs.searchArray.initWithURLParams();
    await this.loadData();
  },
  methods: {
    formatAmount,
    formatSiren(siren) {
      return siren ? formatIdentifier(siren, SIREN_MASK) : '-';
    },
    contactOf(supplier) {
      return [supplier.email, supplier.phone].filter(Boolean).join(' · ') || '-';
    },
    // Les fournisseurs (filtrés, triés, paginés par le serveur) avec leurs totaux, calculés sur tous les achats
    async loadData() {
      this.$refs.table?.startLoading();
      this.data = [];

      const [suppliers, summaries] = await Promise.all([
        SupplierController.getAll(this.$refs.table.buildURLParams()),
        SupplierController.getSummary()
      ]);

      if (!suppliers.errors) {
        const summaryById = new Map((summaries && !summaries.errors ? summaries : []).map(summary => [String(idOf(summary)), summary]));
        this.data.push(...suppliers.map(supplier => ({
          ...supplier,
          id: idOf(supplier),
          summary: summaryById.get(String(idOf(supplier))) ?? EMPTY_SUMMARY
        })));
      }

      this.$refs.table?.stopLoading();
    },
    async remove(supplier) {
      const res = await this.$refs.deleteConfirmModal.displayChoice(() => SupplierController.delete(supplier.id));
      if (res !== false && !(res && res.errors)) {
        (new NotificationService).addNotification("success", "Fournisseur supprimé");
        await this.loadData();
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';
@require '~/src/common/style/businessTable.styl';

.button-icon
  margin-right 10px

.amount
  text-align right
  font-variant-numeric tabular-nums

.overdue
  color #dc2626
  font-weight 600

.to-review
  margin-left 8px
  padding 1px 8px
  border-radius 20px
  background #fff3cd
  font-size 0.8em
  font-weight 600
</style>
